<template>
  <VContainer
    fluid
    class="pa-0"
    :style="{ '--nav-bottom': `${navBottom}px` }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <!-- Adding to a wishlist confirms itself only visually, so screen-reader users
         get no notice it happened (WCAG 4.1.3). This span is the announcement.

         It sits out here, not in the drawer below, and is always rendered. Both
         details are load-bearing: a live region has to be in the accessibility
         tree BEFORE its text changes, and the drawer is v-model gated on
         showDrawer, so anything inside it mounts already-populated and is
         announced inconsistently (NVDA/JAWS generally stay silent). Same trap
         documented in pages/agent/linking.vue. It cannot live in ListMenu
         either — that popup closes before the add resolves.

         .sr-only is position:absolute + clipped, so it adds no layout box. -->
    <span
      class="sr-only"
      role="status"
      aria-live="polite"
      data-testid="wishlist-live-region"
    >{{ wishlistAnnouncement }}</span>
    <VNavigationDrawer
      v-model="showDrawer"
      temporary
      :scrim="false"
      location="right"
      width="360"
      :height="isWishlistNotification ? 'auto' : '100%'"
      role="complementary"
      :class="['notification-provider', { 'wishlist-notification': isWishlistNotification }]"
      :style="drawerStyle"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <Component
        :is="notificationComponent"
        v-bind="notification?.props"
        @terminate-notification="terminateNotification"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      />
    </VNavigationDrawer>
  </VContainer>
</template>


<script lang="ts" setup>
import { h } from 'vue'
import CartErrorNotification from './cart/CartErrorNotification.vue'
import CartMergedNotification from '~/components/notification/cart/CartMergedNotification.vue'
import CartItemAddedNotification from '~/components/notification/cart/CartItemAddedNotification.vue'
import CartStateNotification from '~/components/notification/cart/CartStateNotification.vue'
import WishlistItemAddedNotification from '~/components/notification/wishlist/WishlistItemAddedNotification.vue'
import { NotificationEventState, NotificationType } from '~/types/notifications'
import { useNotificationsStore } from '~/stores/notifications'
import { measureNavBottom } from '~/utils/layout'

const notificationsStore = useNotificationsStore()
const layers = useHeaderLayers()
const notification = computed(() => {
  return notificationsStore.notification
})

const terminateNotification = () => {
  if (notification.value) {
    notificationsStore.replace({
      state: NotificationEventState.NotificationClose
    })
  }
}
const emptyComponent = h('div')
const notificationComponent = computed(() => {
  switch (notification.value?.type) {
    case NotificationType.CartMerged:
      return CartMergedNotification
    case NotificationType.CartItemAdded:
      return CartItemAddedNotification
    case NotificationType.CartState:
      return CartStateNotification
    case NotificationType.WishlistItemAdded:
      return WishlistItemAddedNotification
    case NotificationType.Error:
      notificationsStore.pause()
      return CartErrorNotification
    default:
      return emptyComponent
  }
})

const isWishlistNotification = computed(() => {
  return notification.value?.type === NotificationType.WishlistItemAdded
})

// The store already puts the localized sentence on the notification (`text`,
// built from notifications.wishlist.text.* in ListMenu), so the announcement
// reuses it rather than composing a second copy that could drift. Empty for
// every other notification type: the region stays mounted and silent.
// Route-blacklisted pages (cart, checkout, ...) never raise the notification at
// all, so AT users match sighted users there rather than diverging.
const wishlistAnnouncement = computed(() => {
  if (!isWishlistNotification.value) {
    return ''
  }
  // Notification<P = object> leaves `props` untyped, so `.text` has to be
  // reached through a cast; the runtime check below is what actually guards it.
  const text = (notification.value?.props as { text?: string } | undefined)?.text
  return typeof text === 'string' ? text : ''
})

const isDrawerHovered = ref(false)

const showDrawer = computed({
  get: () => {
    return !!notification.value && notification.value?.state === NotificationEventState.NotificationOpen
  },
  set: (value) => {
    if (!value && notification.value && !isDrawerHovered.value) {
      notificationsStore.replace({
        state: NotificationEventState.NotificationClose
      })
    }
  }
})
const DRAWER_LEAVE_GRACE_MS = 200
let drawerLeaveTimer: ReturnType<typeof setTimeout> | null = null

const cancelDrawerLeaveTimer = () => {
  if (drawerLeaveTimer) {
    clearTimeout(drawerLeaveTimer)
    drawerLeaveTimer = null
  }
}

const onMouseEnter = () => {
  cancelDrawerLeaveTimer()
  isDrawerHovered.value = true
  notificationsStore.setHovered(true, 'drawer')
}

const onMouseLeave = () => {
  cancelDrawerLeaveTimer()
  drawerLeaveTimer = setTimeout(() => {
    isDrawerHovered.value = false
    notificationsStore.setHovered(false, 'drawer')
    drawerLeaveTimer = null
  }, DRAWER_LEAVE_GRACE_MS)
}

// Keep the fixed drawer flush with the live bottom edge of the sticky header.
// ResizeObserver handles header collapse/expansion; scroll and resize cover
// viewport-relative movement caused by content above the header.
const navBottom = ref(0)
// Shared with useProductFilters, which offsets its scroll target by the same edge. The query
// and the clamp lived in both before AB#63280.
const updateNavBottom = () => {
  navBottom.value = measureNavBottom()
}
let navResizeObserver: ResizeObserver | null = null
const drawerStyle = computed(() => ({
  // Vuetify writes its overlay z-index inline, so this must also be inline.
  zIndex: 2147483643,
  top: `${navBottom.value}px`,
  ...(isWishlistNotification.value ? {} : { height: 'auto' }),
}))

onMounted(() => {
  updateNavBottom()
  const nav = document.querySelector('.nav-component') as HTMLElement | null
  if (nav && typeof ResizeObserver !== 'undefined') {
    navResizeObserver = new ResizeObserver(updateNavBottom)
    navResizeObserver.observe(nav)
  }
  window.addEventListener('scroll', updateNavBottom, { passive: true })
  window.addEventListener('resize', updateNavBottom, { passive: true })
})

const handleDocumentClick = (event: MouseEvent) => {
  if (!showDrawer.value) { return }
  const drawer = document.querySelector('.notification-provider')
  const target = event.target as Node | null
  if (drawer && target && !drawer.contains(target)) {
    terminateNotification()
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && showDrawer.value) {
    terminateNotification()
  }
}

watch(showDrawer, (open) => {
  if (open) {
    layers.open('cartNotification')
    nextTick(() => {
      if (!showDrawer.value) { return }
      document.addEventListener('click', handleDocumentClick, true)
      document.addEventListener('keydown', handleKeydown)
    })
  } else {
    layers.close('cartNotification')
    document.removeEventListener('click', handleDocumentClick, true)
    document.removeEventListener('keydown', handleKeydown)
  }
})

watch(layers.activeLayer, (layer) => {
  if (layer !== 'cartNotification' && showDrawer.value) {
    terminateNotification()
  }
})

onUnmounted(() => {
  cancelDrawerLeaveTimer()
  navResizeObserver?.disconnect()
  navResizeObserver = null
  window.removeEventListener('scroll', updateNavBottom)
  window.removeEventListener('resize', updateNavBottom)
  document.removeEventListener('click', handleDocumentClick, true)
  document.removeEventListener('keydown', handleKeydown)
  layers.close('cartNotification')
})

// handle adding item to cart to scroll to the top of the drawer
watch(() => notification.value?.props, async () => {
  await nextTick()
  const scrollable = document.querySelector('.scrollable-container')
  if (scrollable) {
    scrollable.scrollTop = 0
  }
})
</script>

<style lang="scss" scoped>
.notification-provider {
  position: fixed;
  right: 0;
  border-inline-start-width: 0;
  &:not(.wishlist-notification) {
    bottom: var(--pwa-control-bar-offset, 0px) !important;
  }
}
</style>
